// react-redux imports
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { RootState } from "app/store";
import { setAuthData } from "features/auth/authSlice";

import { SeparatedWaste } from "features/calculator/householdHabits/householdHabitsSlice";
import { CalculatorResult, UserAuthResponse } from "features/auth/types";
import { trackRegister } from "features/cookie-banner/gtm-functions";

import { EMAIL_PATTERN } from "common/constants/regex";
import { API_URL } from "common/constants/config";
import FlexColumn from "common/components/styled-components/FlexColumn";
import Input from "common/components/Input";

import ButtonGroup from "./styled-components/ButtonGroup";
import TermsConfirmation from "../TermsConfirmation";
import FormActionGroup from "../styled-components/ActionGroup";
import CustomButton from "../styled-components/ActionButton";
import Form from "../styled-components/Form";

interface SignUpFormProps {
  switchToSignIn(): void;
  successCallback(resultResponse: CalculatorResult): void;
}

interface FormFields {
  email: string;
  name: string;
  password: string;
  repeatPassword: string;
  acceptTerms: string;
}

function SignUpForm({ switchToSignIn, successCallback }: SignUpFormProps) {
  const dispatch = useDispatch();
  const calculator = useSelector((state: RootState) => state.calculator);
  const {
    register,
    setError,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>();

  async function submitCallback(data: FormFields) {
    try {
      const {
        householdHabits: { separatedWaste: separatedWasteObject },
      } = calculator;

      const separatedWaste = Object.entries(separatedWasteObject).reduce(
        (acc: Array<keyof SeparatedWaste>, curr: [string, boolean]) => {
          const [key, value] = curr;
          if (value) acc.push(key as keyof SeparatedWaste);
          return acc;
        },
        []
      );
      const response = await axios.post<UserAuthResponse>(
        `${API_URL}/auth/sign-up`,
        {
          name: data.name,
          email: data.email,
          password: data.password,
          calculator: {
            ...calculator,
            householdHabits: {
              ...calculator.householdHabits,
              separatedWaste,
            },
          },
        }
      );

      dispatch(
        setAuthData({
          role: "user",
          email: response.data.user.email,
          name: response.data.user.name,
        })
      );
      trackRegister();
      successCallback(response.data.user.result);
    } catch (err: any) {
      switch (err?.response?.data?.code) {
        case "409-001":
          setError(
            "email",
            {
              type: "emailAlreadyInUse",
              message: "Este correo electrónico se encuentra en uso",
            },
            { shouldFocus: true }
          );
          break;

        default:
          console.error();
      }
    }
  }

  return (
    <Form onSubmit={handleSubmit(submitCallback)} autoComplete="off">
      <FlexColumn gap={8}>
        <Input
          registerReturn={register("email", {
            required: "Introduce tu correo electrónico",
            pattern: {
              value: EMAIL_PATTERN,
              message: "Correo electrónico inválido",
            },
          })}
          error={errors.email}
          type="email"
          placeholder="Correo electrónico*"
        />
        <Input
          registerReturn={register("name", {
            required: "Introduce tu nombre",
          })}
          error={errors.name}
          type="text"
          placeholder="Nombre*"
        />
        <Input
          registerReturn={register("password", {
            required: "Introduce una contraseña",
            minLength: {
              value: 8,
              message: "La contraseña debe tener al menos 8 caracteres",
            },
            maxLength: {
              value: 255,
              message: "La contraseña debe tener menos de 256 caracteres",
            },
          })}
          error={errors.password}
          type="password"
          placeholder="Contraseña*"
        />
        <Input
          registerReturn={register("repeatPassword", {
            required: "Repite la contraseña",
            validate: (value) =>
              getValues("password") === value || "Las contraseñas no coinciden",
          })}
          error={errors.repeatPassword}
          type="password"
          placeholder="Repetir contraseña*"
        />
      </FlexColumn>
      <FormActionGroup>
        <TermsConfirmation
          registerReturn={register("acceptTerms", { required: true })}
        />
        <ButtonGroup>
          <CustomButton type="submit">Registrarme</CustomButton>
          <CustomButton type="button" onClick={switchToSignIn} secondary>
            Iniciar sesión
          </CustomButton>
        </ButtonGroup>
      </FormActionGroup>
    </Form>
  );
}

export default SignUpForm;
